import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../components/ui/Toast';
import { Button, Card, Badge, DataTable } from '../components/ui';

const TIPO_AREA_LABELS = {
  100000000: 'Ala',
  100000001: 'Piano',
  100000002: 'Nucleo',
  100000003: 'Stanza'
};

const TIPO_AREA_COLORS = {
  100000000: 'bg-purple-100 text-purple-800',
  100000001: 'bg-blue-100 text-blue-800',
  100000002: 'bg-green-100 text-green-800',
  100000003: 'bg-orange-100 text-orange-800'
};

const TIPO_AREA_ICONS = {
  100000000: '🏛️',
  100000001: '🏢',
  100000002: '🔵',
  100000003: '🚪'
};

function DettaglioAreaPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, apiRequest } = useAuth();
  const { showToast } = useToast();

  const [area, setArea] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [apparecchiature, setApparecchiature] = useState([]);
  const [loadingApparecchiature, setLoadingApparecchiature] = useState(false);

  const isAdmin = user?.ruolo === 100000000;
  const isRspp = user?.ruolo === 100000001;
  const isDirezione = user?.ruolo === 100000002;
  const isDirettoreRsa = user?.ruolo === 100000003;
  const canEdit = isAdmin || isRspp || isDirezione || isDirettoreRsa;
  const canDelete = isAdmin;

  useEffect(() => {
    const fetchArea = async () => {
      setLoading(true);
      try {
        const data = await apiRequest(`/aree/${id}`);
        setArea(data);
      } catch (error) {
        console.error('Error fetching area:', error);
        showToast('Errore nel caricamento dell\'area', 'error');
        navigate('/rsa');
      } finally {
        setLoading(false);
      }
    };

    const fetchApparecchiature = async () => {
      setLoadingApparecchiature(true);
      try {
        const data = await apiRequest(`/aree/${id}/apparecchiature`);
        setApparecchiature(data.items || []);
      } catch (error) {
        console.error('Error fetching apparecchiature:', error);
      } finally {
        setLoadingApparecchiature(false);
      }
    };

    fetchArea();
    fetchApparecchiature();
  }, [id, apiRequest, navigate, showToast]);

  const handleDelete = async () => {
    if (!window.confirm('Sei sicuro di voler eliminare questa area? L\'operazione non e\' reversibile.')) {
      return;
    }

    setDeleting(true);
    try {
      await apiRequest(`/aree/${id}`, { method: 'DELETE' });
      showToast('Area eliminata con successo', 'success');
      navigate(`/rsa/${area.rsaId}?tab=aree`);
    } catch (error) {
      console.error('Error deleting area:', error);
      showToast(error.message || 'Errore durante l\'eliminazione', 'error');
    } finally {
      setDeleting(false);
    }
  };

  const figliColumns = [
    {
      key: 'tipoArea',
      label: 'Tipo',
      render: (row) => (
        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${TIPO_AREA_COLORS[row.tipoArea]}`}>
          {TIPO_AREA_ICONS[row.tipoArea]} {row.tipoAreaLabel}
        </span>
      )
    },
    {
      key: 'nome',
      label: 'Nome',
      render: (row) => (
        <Link to={`/aree/${row.id}`} className="text-primary-600 hover:underline font-medium">
          {row.nome}
        </Link>
      )
    },
    {
      key: 'codice',
      label: 'Codice',
      render: (row) => row.codice || '-'
    },
    {
      key: 'capacita',
      label: 'Capacita\'',
      render: (row) => row.capacita ?? '-'
    },
    {
      key: 'attivo',
      label: 'Stato',
      render: (row) => (
        <Badge variant={row.attivo ? 'success' : 'default'}>
          {row.attivo ? 'Attiva' : 'Inattiva'}
        </Badge>
      )
    }
  ];

  const apparecchiatureColumns = [
    {
      key: 'tipoLabel',
      label: 'Tipo',
      render: (row) => (
        <span className="text-sm">{row.tipoLabel || '-'}</span>
      )
    },
    {
      key: 'marca',
      label: 'Marca/Modello',
      render: (row) => (
        <Link to={`/apparecchiature/${row.id}`} className="text-primary-600 hover:underline font-medium">
          {row.marca || ''} {row.modello || ''}
        </Link>
      )
    },
    {
      key: 'matricolaInterna',
      label: 'Matricola',
      render: (row) => row.matricolaInterna ? (
        <span className="font-mono bg-gray-100 px-2 py-0.5 rounded text-xs">{row.matricolaInterna}</span>
      ) : '-'
    },
    {
      key: 'statoLabel',
      label: 'Stato',
      render: (row) => {
        const colors = {
          100000000: 'bg-green-100 text-green-800',
          100000001: 'bg-red-100 text-red-800',
          100000002: 'bg-yellow-100 text-yellow-800',
          100000003: 'bg-gray-100 text-gray-800'
        };
        return (
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${colors[row.stato] || 'bg-gray-100 text-gray-800'}`}>
            {row.statoLabel || '-'}
          </span>
        );
      }
    }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="spinner w-12 h-12"></div>
      </div>
    );
  }

  if (!area) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Area non trovata</p>
        <Button onClick={() => navigate('/rsa')} className="mt-4">
          Torna alla lista RSA
        </Button>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      {/* Breadcrumb con percorso gerarchico */}
      <div className="flex items-center gap-2 text-sm text-gray-500 mb-4 flex-wrap">
        <Link to="/rsa" className="hover:text-primary-600">RSA</Link>
        <span>/</span>
        <Link to={`/rsa/${area.rsaId}`} className="hover:text-primary-600">{area.rsaNome}</Link>
        <span>/</span>
        <Link to={`/rsa/${area.rsaId}?tab=aree`} className="hover:text-primary-600">Aree</Link>

        {/* Percorso gerarchico */}
        {area.percorso && area.percorso.map((p, idx) => (
          <React.Fragment key={p.id}>
            <span>/</span>
            <Link to={`/aree/${p.id}`} className="hover:text-primary-600">
              {p.nome}
            </Link>
          </React.Fragment>
        ))}

        <span>/</span>
        <span className="text-gray-900 font-medium">{area.nome}</span>
      </div>

      {/* Header */}
      <div className="flex justify-between items-start mb-6">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <span className="text-3xl">{TIPO_AREA_ICONS[area.tipoArea]}</span>
            <h1 className="text-2xl font-bold text-gray-900">{area.nome}</h1>
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${TIPO_AREA_COLORS[area.tipoArea]}`}>
              {area.tipoAreaLabel}
            </span>
            <Badge variant={area.attivo ? 'success' : 'default'} dot>
              {area.attivo ? 'Attiva' : 'Inattiva'}
            </Badge>
          </div>
          {area.codice && (
            <p className="text-gray-600">Codice: <span className="font-mono bg-gray-100 px-2 py-0.5 rounded">{area.codice}</span></p>
          )}
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => navigate(`/rsa/${area.rsaId}?tab=aree`)}>
            Indietro
          </Button>
          {canEdit && (
            <Button onClick={() => navigate(`/aree/nuovo?id=${id}`)}>
              Modifica
            </Button>
          )}
          {canDelete && (
            <Button
              variant="danger"
              onClick={handleDelete}
              disabled={deleting || (area.figli && area.figli.length > 0)}
              title={area.figli && area.figli.length > 0 ? 'Elimina prima le aree figlie' : ''}
            >
              {deleting ? 'Eliminazione...' : 'Elimina'}
            </Button>
          )}
        </div>
      </div>

      {/* Percorso gerarchico visuale */}
      {area.percorso && area.percorso.length > 0 && (
        <Card className="mb-6 p-4 bg-gradient-to-r from-gray-50 to-white">
          <h3 className="text-sm font-medium text-gray-500 mb-3">Posizione nella struttura</h3>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-lg">🏥</span>
            <Link to={`/rsa/${area.rsaId}`} className="font-medium text-primary-600 hover:underline">
              {area.rsaNome}
            </Link>

            {area.percorso.map((p, idx) => (
              <React.Fragment key={p.id}>
                <span className="text-gray-400 mx-1">&rarr;</span>
                <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${TIPO_AREA_COLORS[p.tipoArea]}`}>
                  {TIPO_AREA_ICONS[p.tipoArea]}
                  <Link to={`/aree/${p.id}`} className="hover:underline">
                    {p.nome}
                  </Link>
                </span>
              </React.Fragment>
            ))}

            <span className="text-gray-400 mx-1">&rarr;</span>
            <span className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-sm font-semibold ${TIPO_AREA_COLORS[area.tipoArea]} ring-2 ring-offset-2 ring-gray-200`}>
              {TIPO_AREA_ICONS[area.tipoArea]} {area.nome}
            </span>
          </div>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Dettagli Area */}
        <Card>
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <span>📋</span> Dettagli
          </h2>
          <dl className="space-y-3">
            <div className="flex justify-between">
              <dt className="text-gray-500">Nome</dt>
              <dd className="font-medium">{area.nome}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-gray-500">Tipo</dt>
              <dd>
                <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${TIPO_AREA_COLORS[area.tipoArea]}`}>
                  {TIPO_AREA_ICONS[area.tipoArea]} {area.tipoAreaLabel}
                </span>
              </dd>
            </div>
            {area.codice && (
              <div className="flex justify-between">
                <dt className="text-gray-500">Codice</dt>
                <dd className="font-mono bg-gray-100 px-2 py-0.5 rounded text-sm">{area.codice}</dd>
              </div>
            )}
            {area.ordine !== undefined && area.ordine !== null && (
              <div className="flex justify-between">
                <dt className="text-gray-500">Ordine</dt>
                <dd className="font-medium">{area.ordine}</dd>
              </div>
            )}
            <div className="flex justify-between">
              <dt className="text-gray-500">Stato</dt>
              <dd>
                <Badge variant={area.attivo ? 'success' : 'default'}>
                  {area.attivo ? 'Attiva' : 'Inattiva'}
                </Badge>
              </dd>
            </div>
          </dl>
        </Card>

        {/* Dati Tecnici */}
        <Card>
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <span>📐</span> Dati Tecnici
          </h2>
          <dl className="space-y-3">
            {area.capacita !== undefined && area.capacita !== null && (
              <div className="flex justify-between">
                <dt className="text-gray-500">
                  {area.tipoArea === 100000003 ? 'Posti letto' : 'Capacita\''}
                </dt>
                <dd className="font-medium text-lg">{area.capacita}</dd>
              </div>
            )}
            {area.superficie !== undefined && area.superficie !== null && (
              <div className="flex justify-between">
                <dt className="text-gray-500">Superficie</dt>
                <dd className="font-medium">{area.superficie} mq</dd>
              </div>
            )}
            {area.parentNome && (
              <div className="flex justify-between">
                <dt className="text-gray-500">Appartiene a</dt>
                <dd>
                  <Link to={`/aree/${area.parentId}`} className="text-primary-600 hover:underline">
                    {area.parentTipoLabel}: {area.parentNome}
                  </Link>
                </dd>
              </div>
            )}
          </dl>

          {/* Empty state per dati tecnici */}
          {!area.capacita && !area.superficie && (
            <p className="text-gray-400 text-sm italic mt-4">
              Nessun dato tecnico specificato
            </p>
          )}
        </Card>
      </div>

      {/* Descrizione */}
      {area.descrizione && (
        <Card className="mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <span>📄</span> Descrizione
          </h2>
          <p className="text-gray-700">{area.descrizione}</p>
        </Card>
      )}

      {/* Note */}
      {area.note && (
        <Card className="mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <span>📝</span> Note
          </h2>
          <p className="text-gray-700 whitespace-pre-wrap">{area.note}</p>
        </Card>
      )}

      {/* Aree figlie */}
      {area.figli && area.figli.length > 0 && (
        <Card>
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Aree contenute</h2>
              <p className="text-sm text-gray-500">
                {area.figli.length} {area.figli.length === 1 ? 'area' : 'aree'} all'interno di {area.nome}
              </p>
            </div>
            {canEdit && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate(`/aree/nuovo?rsaId=${area.rsaId}&parentId=${area.id}`)}
              >
                + Aggiungi area figlia
              </Button>
            )}
          </div>

          <DataTable
            data={area.figli}
            columns={figliColumns}
            onRowClick={(row) => navigate(`/aree/${row.id}`)}
          />
        </Card>
      )}

      {/* Empty state figli + azione */}
      {(!area.figli || area.figli.length === 0) && area.tipoArea !== 100000003 && (
        <Card>
          <div className="text-center py-8">
            <div className="text-4xl mb-3">
              {area.tipoArea === 100000000 && '🏢'}
              {area.tipoArea === 100000001 && '🔵'}
              {area.tipoArea === 100000002 && '🚪'}
            </div>
            <p className="text-gray-500 mb-4">
              Nessuna area contenuta in {area.nome}
            </p>
            {canEdit && (
              <Button
                onClick={() => navigate(`/aree/nuovo?rsaId=${area.rsaId}&parentId=${area.id}`)}
              >
                Aggiungi {
                  area.tipoArea === 100000000 ? 'Piano' :
                  area.tipoArea === 100000001 ? 'Nucleo' :
                  'Stanza'
                }
              </Button>
            )}
          </div>
        </Card>
      )}

      {/* Apparecchiature in questa area */}
      <Card className="mt-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <span>⚙️</span> Apparecchiature
            </h2>
            <p className="text-sm text-gray-500">
              {apparecchiature.length > 0
                ? `${apparecchiature.length} apparecchiatur${apparecchiature.length === 1 ? 'a' : 'e'} in questa area`
                : 'Nessuna apparecchiatura in questa area'
              }
            </p>
          </div>
          {canEdit && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate(`/apparecchiature/nuovo?rsaId=${area.rsaId}&areaId=${area.id}`)}
            >
              + Aggiungi apparecchiatura
            </Button>
          )}
        </div>

        {loadingApparecchiature ? (
          <div className="flex justify-center py-8">
            <div className="spinner w-8 h-8"></div>
          </div>
        ) : apparecchiature.length > 0 ? (
          <DataTable
            data={apparecchiature}
            columns={apparecchiatureColumns}
            onRowClick={(row) => navigate(`/apparecchiature/${row.id}`)}
          />
        ) : (
          <div className="text-center py-8 text-gray-400">
            <div className="text-4xl mb-3">⚙️</div>
            <p>Nessuna apparecchiatura registrata in questa area</p>
            {canEdit && (
              <Button
                variant="outline"
                size="sm"
                className="mt-4"
                onClick={() => navigate(`/apparecchiature/nuovo?rsaId=${area.rsaId}&areaId=${area.id}`)}
              >
                Aggiungi la prima apparecchiatura
              </Button>
            )}
          </div>
        )}
      </Card>

      {/* Metadata */}
      <div className="mt-6 pt-4 border-t text-xs text-gray-400 flex justify-between">
        <span>Creato: {area.createdOn ? new Date(area.createdOn).toLocaleString('it-IT') : '-'}</span>
        <span>Modificato: {area.modifiedOn ? new Date(area.modifiedOn).toLocaleString('it-IT') : '-'}</span>
      </div>
    </div>
  );
}

export default DettaglioAreaPage;
